<template>
  <CCard class="contract-volume-discount">
    <CCardHeader>
      <div class="section-heading">
        <div>
          <strong>人数割</strong>
          <p>この契約企業に適用する人数割を設定します。</p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <!--
        カードの頭に出すのは、読み込みの失敗だけ（初期特別価格のタブと同じ）。
        保存の結果と送る前の断りは、［保存する］のすぐ下に出す（帯の表の下にあり、頭は見えない）。

        **ただし、保存が通ったあとの読み直しで落ちたときは、［保存する］ごと描かれない。**
        そのときは「保存しました」（V29）をカードの頭に出す（点検 7）。出さないと
        「読み込めませんでした」だけが残り、保存できたのかが分からない（最低月額・初期特別価格の
        タブは同じ場合でも成功の文が残る）。
      -->
      <ActionResultMessage v-if="!showsForm" :text="successMessage" type="success"/>
      <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>

      <div v-if="loading" class="contract-volume-discount__loading">読み込んでいます…</div>

      <!--
        **契約の無い会社には、選択肢も保存も出さない**（最低月額のタブと同じ判定・同じ文）。
        back は契約の無い会社に何も返さず、保存も「対象の契約情報が見つかりません。」で断る。
      -->
      <CAlert v-else-if="loadedAccountId !== null && !linked" color="warning">
        この契約企業は、まだ契約が登録されていません。
        先に契約を保存してから設定してください。
      </CAlert>

      <!--
        **読めた会社の値があるときだけ、選択肢と保存を出す。**
        読めなかったときに空の選択肢を出すと、「プランに従う」のまま保存できてしまい、
        入っていた「適用しない」や独自の帯を消す。
      -->
      <template v-else-if="loadedAccountId !== null">
        <p v-if="!canEdit" class="text-muted">人数割の変更には「契約情報を更新」権限が必要です。</p>

        <div class="volume-choices">
          <label class="volume-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="volume-discount-mode"
              value="PLAN"
              :disabled="!canEdit"
            />
            <!--
              **帯ごとに折り返さない塊で描く**（請求管理の要約と同じ。点検 14）。
              1つの span だと、狭い画面や帯の多い会社で「10%」と「OFF」の間など帯の途中で切れる。
              要素の間に空白を入れない（文が「プランに従う（1〜10名：0%OFF / …）」のまま続くように）。
            -->
            <span class="volume-choice-plan"><span
              v-for="(piece, index) in planChoicePieces"
              :key="index"
              :class="{ 'volume-choice-part': piece.part }"
            >{{ piece.text }}</span></span>
          </label>
          <label class="volume-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="volume-discount-mode"
              value="NONE"
              :disabled="!canEdit"
            />
            <span>人数割を適用しない</span>
          </label>
          <label class="volume-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="volume-discount-mode"
              value="CUSTOM"
              :disabled="!canEdit"
            />
            <span>この会社独自の帯を設定する</span>
          </label>

          <!--
            **帯の表は「この会社独自」を選んだときだけ出す。**他を選んでいるのに帯が見えていると、
            その帯が効いていると読める。試算の定価・基本料は、今月の請求に使うプランの今月1日時点の料金。
          -->
          <div v-if="form.mode === 'CUSTOM'" class="volume-custom">
            <VolumeDiscountTierEditor
              v-model="form.tiers"
              :price-per-user="planPricePerUser"
              :base-price="planBasePrice"
              :disabled="!canEdit || saving"
              :show-clear="false"
            />
          </div>
        </div>

        <div class="volume-remarks">
          <label for="volume-discount-remarks">備考</label>
          <CInput id="volume-discount-remarks" v-model.trim="form.remarks" maxlength="200" :disabled="!canEdit"/>
        </div>

        <!--
          **当てない月を、保存する前に書いておく。**今月が初期特別価格の期間なら目に付く色にする
          （最低月額のタブと同じ）。「設定したのに今月の見込みが下がらない」と読まれるのを防ぐ。
        -->
        <p
          class="volume-note"
          :class="{ 'volume-note--warning': introPriceActive }"
        >初期特別価格の適用期間中、無料プラン、無料試用中の月は人数割を適用しません。</p>

        <div v-if="canEdit" class="contract-volume-discount__actions">
          <CButton
            type="button"
            color="primary"
            :disabled="saving"
            @click="save"
          >{{ saving ? '保存しています…' : '保存する' }}</CButton>
        </div>
        <ActionResultMessage :text="successMessage" type="success"/>
        <ActionResultMessage :text="errorMessage" type="danger"/>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import api from '@/services/accessManagement'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import VolumeDiscountTierEditor from './VolumeDiscountTierEditor.vue'
import { SUMMARY_SEPARATOR, normalizeTiers, summaryParts, summaryText, tierProblem } from '@/utils/volumeDiscount'
import { safeResponseMessage, safeErrorDigest } from '@/utils/safeResponseMessage'

/** 選べる3つ。back も同じ3つしか受け付けない（それ以外は V21 で断る）。 */
const MODES = ['PLAN', 'NONE', 'CUSTOM']

/* 選択肢1の文（V24）。{要約} に、今月の請求に使うプランの帯の要約（V02／V03）が入る。 */
const PLAN_CHOICE_TEMPLATE = 'プランに従う（{要約}）'

/** 帯を入力の形（文字列・人数の昇順）にする。 */
function asInputRows (tiers) {
  return (Array.isArray(tiers) ? tiers : [])
    .slice()
    .sort((a, b) => Number(a.fromSeat) - Number(b.fromSeat))
    .map(tier => ({ fromSeat: String(tier.fromSeat), discountRate: String(tier.discountRate) }))
}

/** 「1名から0%」の1行（割引なし）。 */
function oneFlatTier () {
  return [{ fromSeat: '1', discountRate: '0' }]
}

/**
 * ボリュームディスカウントの、契約ごとの設定（0108）。
 *
 * <h2>何を決めるか</h2>
 *
 *   PLAN   … プランの帯に従う（設定の行が無いときもこれ）
 *   NONE   … この会社には当てない（人数にかかわらず定価）
 *   CUSTOM … この会社だけの帯を使う
 * 利用者の「アプリ上もカスタマイズできるようにしてほしい」（2026-09-26）。
 * 設計：C:\biz\claude\人数帯の割引率_設計_2026-09-27.md の決まり6。
 *
 * <h2>どこから見えるか</h2>
 *
 * 運営メニュー「PREIKA運営」→「契約企業一覧」→ 会社の［管理］→「最低月額」のすぐ右のタブ。
 *
 * <h2>作り</h2>
 *
 * ContractMinimumCharge と同じ（会社の番号と選び直しの番号で読み直す・読み始めたら前の会社の値を
 * 消す・保存を始めた会社を覚えて、別の会社へ移っていたら何もしない）。
 * 結果の出し場所だけは初期特別価格のタブと同じく［保存する］のすぐ下（帯の表で縦に長くなるため）。
 */
export default {
  name: 'ContractVolumeDiscount',

  components: { ActionResultMessage, VolumeDiscountTierEditor },

  props: {
    accountId: { type: Number, required: true },
    /** 契約を変えられるか（CONTRACT_UPDATE）。false なら読むだけ。 */
    canEdit: { type: Boolean, default: false },
    /**
     * 読み直しの番号。親は「会社を選び直した回」と「同じ会社の［管理］の中で契約・
     * 初期特別価格を保存した回」を組にして渡す（最低月額のタブと同じ）。
     */
    reloadKey: { type: [Number, String], default: 0 }
  },

  data () {
    return {
      form: this.空の入力(),
      planTiers: [],
      planPricePerUser: null,
      planBasePrice: null,
      introPriceActive: false,
      linked: false,
      // **いま画面に出ている値は、どの会社のものか。**（最低月額のタブと同じ）
      loadedAccountId: null,
      loading: true,
      saving: false,
      loadError: '',
      errorMessage: '',
      successMessage: ''
    }
  },

  computed: {
    loadKey () {
      return this.accountId + ':' + this.reloadKey
    },

    /** 選択肢1の文に、今月の請求に使うプランの帯の要約を差し込む。 */
    planChoiceText () {
      return PLAN_CHOICE_TEMPLATE.replace('{要約}', summaryText(this.planTiers))
    },

    /**
     * 選択肢1の文（V24）を、帯（part）と、それ以外（「プランに従う（」・つなぎ・「）」）に分けたもの。
     * 帯は途中で折り返さない。帯が無い・率が全部0なら「プランに従う（割引なし）」の1つ。
     * つなげると planChoiceText と同じ文になる。
     */
    planChoicePieces () {
      const parts = summaryParts(this.planTiers)
      if (!parts.length) return [{ text: this.planChoiceText, part: false }]
      const [head, tail] = PLAN_CHOICE_TEMPLATE.split('{要約}')
      const pieces = [{ text: head, part: false }]
      parts.forEach((part, index) => {
        if (index > 0) pieces.push({ text: SUMMARY_SEPARATOR, part: false })
        pieces.push({ text: part, part: true })
      })
      pieces.push({ text: tail, part: false })
      return pieces
    },

    /**
     * 選択肢と［保存する］を描いているか（テンプレートの v-if の並びと同じ条件）。
     * 描いていないときは、保存の結果をカードの頭に出す。
     */
    showsForm () {
      return !this.loading && this.loadedAccountId !== null && this.linked
    }
  },

  watch: {
    'form.mode' (mode) {
      /*
       * **「この会社独自」を選んだとき、帯の表が空なら、プランの帯から始める。**
       * 独自の帯は、たいていプランの帯を少し変えたもの。プランに帯が無ければ「1名から0%」。
       * 一度入れた帯は、ほかを選んで戻っても残す（入れかけを消さない。送るのは独自のときだけ）。
       */
      if (mode === 'CUSTOM' && !this.form.tiers.length) {
        this.form.tiers = this.planTiers.length ? asInputRows(this.planTiers) : oneFlatTier()
      }
    },

    loadKey: {
      immediate: true,
      handler () {
        /*
         * **別の会社へ移ったときだけ、前の会社の保存を捨てる**（最低月額のタブと同じ）。
         * 同じ会社の選び直しでは捨てない。
         */
        if (this.$_watchedAccountId !== this.accountId) {
          this.$_saveOp = (this.$_saveOp || 0) + 1
          this.saving = false
        }
        this.$_watchedAccountId = this.accountId
        this.load()
      }
    }
  },

  methods: {
    空の入力 () {
      return { mode: 'PLAN', tiers: [], remarks: '' }
    },

    数か空 (value) {
      return value === null || value === undefined || String(value).trim() === ''
        ? null : Number(value)
    },

    /**
     * **会社を切り替えたら、前の会社の返事は捨てる**（最低月額のタブと同じ）。
     * 読み始めたらすぐ入力を空にし、どの会社の値かの印（loadedAccountId）も外す。
     */
    async load () {
      const seq = this.$_loadSeq = (this.$_loadSeq || 0) + 1
      const target = this.accountId
      this.loading = true
      this.loadedAccountId = null
      this.loadError = ''
      this.errorMessage = ''
      this.successMessage = ''
      this.form = this.空の入力()
      this.planTiers = []
      this.planPricePerUser = null
      this.planBasePrice = null
      this.introPriceActive = false
      this.linked = false
      const stale = () => seq !== this.$_loadSeq || target !== this.accountId
      try {
        const response = await api.getContractVolumeDiscount(target)
        if (stale()) return
        // **契約の無い会社は、空（null・空の本文）が返る。**最低月額のタブと同じ判定。
        this.linked = !!response
        const current = response && typeof response === 'object' ? response : {}
        const mode = current.mode === null || current.mode === undefined ? 'PLAN' : String(current.mode)
        this.planTiers = Array.isArray(current.planTiers) ? current.planTiers : []
        this.form = {
          mode,
          // 独自の帯があればそれを、無ければ「独自」を選んだときにプランの帯から始める。
          tiers: mode === 'CUSTOM' && Array.isArray(current.tiers) && current.tiers.length
            ? asInputRows(current.tiers)
            : (mode === 'CUSTOM' ? oneFlatTier() : []),
          remarks: current.remarks || ''
        }
        this.planPricePerUser = this.数か空(current.planPricePerUser)
        this.planBasePrice = this.数か空(current.planBasePrice)
        this.introPriceActive = current.introPriceActive === true
        this.loadedAccountId = target
      } catch (error) {
        if (stale()) return
        this.loadError = '人数割を読み込めませんでした。'
        this.$emit('load-error', safeErrorDigest(error))
      } finally {
        if (!stale()) this.loading = false
      }
    },

    /** 保存してよいか。読み込み中・別の会社の値・権限なしでは送らない。 */
    canSend () {
      return this.canEdit && !this.saving && !this.loading && this.linked
        && this.loadedAccountId !== null && this.loadedAccountId === this.accountId
    },

    /** 送る前に、サーバと同じ文で止める。 */
    whatIsWrong () {
      if (MODES.indexOf(this.form.mode) < 0) {
        return '人数割の適用方法を正しく選択してください。'
      }
      if (this.form.mode === 'CUSTOM') return tierProblem(this.form.tiers)
      return ''
    },

    async save () {
      if (!this.canSend()) return
      const wrong = this.whatIsWrong()
      if (wrong) {
        this.errorMessage = wrong
        this.successMessage = ''
        return
      }
      await this.send({
        accountId: this.loadedAccountId,
        mode: this.form.mode,
        // **帯は「この会社独自」のときだけ送る。**他のときに送ると、選んでいない帯が残って見える。
        tiers: this.form.mode === 'CUSTOM' ? normalizeTiers(this.form.tiers) : [],
        remarks: this.form.remarks
      })
    },

    /**
     * **保存を始めた会社を覚えておく**（最低月額のタブと同じ）。
     * 終わったときに別の会社へ移っていたら、いまの画面には何もしない（成功でも失敗でも）。
     */
    async send (payload) {
      const target = this.loadedAccountId
      const op = this.$_saveOp = (this.$_saveOp || 0) + 1
      const moved = () => op !== this.$_saveOp || target !== this.accountId
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.replaceContractVolumeDiscount(payload)
        if (moved()) return
        // **保存できた値を読み直す。**送ったつもりの値ではなく、入っている値を見せる。
        // 読み直しの失敗は load が「読み込めませんでした」としてカードの頭に出す（ここへは来ない）。
        // そのときも下の「保存しました」は出す（［保存する］ごと描かれないので、カードの頭に出る。点検 7）。
        await this.load()
        if (moved()) return
        this.successMessage = '人数割を保存しました。'
      } catch (error) {
        if (moved()) return
        this.errorMessage = safeResponseMessage(
          error, '人数割を保存できませんでした。時間をおいて再度お試しください。')
      } finally {
        if (!moved()) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.contract-volume-discount { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }

.contract-volume-discount__loading { color: #5c6873; }

.volume-choices {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.volume-choice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}

.volume-choice input { margin-top: 0.3rem; }

/* 選択肢1の帯（「11〜30名：10%OFF」）。途中で切らない。つなぎの「 / 」でだけ折り返す。 */
.volume-choice-part { white-space: nowrap; }

/* 選択肢3の下に字下げして置く。どの選択肢の帯かを並びで分からせる。 */
.volume-custom { max-width: 40rem; margin-left: 1.5rem; }

.volume-remarks { max-width: 28rem; }
.volume-remarks label { display: block; font-weight: 600; font-size: 0.8rem; }

.volume-note {
  margin: 0.75rem 0 0;
  color: #6a7f8c;
  font-size: 0.8rem;
}

.volume-note--warning { color: #b54708; }

.contract-volume-discount__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}
</style>
